import moment from "moment";
import AppConstants from "../../constants";
import {
  enumerateDaysBetweenDates,
  getUniqueValues,
  getValueFromArray,
  localTimeString,
} from "../../utils/helpers.utils";
import {
  MetaData,
  Address,
  PosInformation,
  DeliveryInformation,
  PickUpInformation,
  Product,
  Reason,
  Statuses,
} from "../SharedTransform/interface";

import { getFilterAttributeValue } from "../SharedTransform/response.transforms";

export interface CNCOrder {
  id: string;
  orderCode: string;
  consignmentCode: string;
  hasRejectedProducts: boolean;
  retryCount: number;
  metadata: MetaData;
  products: Product[];
  reason: Reason;
  posInformation: PosInformation;
  deliveryInformation: DeliveryInformation;
  pickUpInformation: PickUpInformation;
  statuses: Statuses[];
}
export interface CNCOrdersDataResponse {
  pageNumber: number;
  totalElements: number;
  totalPages: number;
  continuationToken?: string;
  elements: CNCOrder[];
}

export const lastMilePlanningOrdersDataTransform = (
  response: CNCOrdersDataResponse
): any[] => {
  const transformedArray: any[] = [];
  response.elements.forEach((order: CNCOrder) => {
    const {
      id,
      metadata,
      posInformation: { store, storeName },
      deliveryInformation,
      pickUpInformation,
      orderCode,
      products,
      reason,
      consignmentCode,
      statuses,
    } = order;
    // TODO: map/add actual objects too for drilling down content or store in state
    const collectionDate = moment(
      pickUpInformation &&
        pickUpInformation.pickupSlot &&
        pickUpInformation.pickupSlot.startTime,
      AppConstants.DATE_FORMAT_BACKEND
    );
    const customerContactInfo = deliveryInformation.customerContactInfo || {
      address: {},
    };
    const transactionDate = moment(
      metadata.transactionDate,
      AppConstants.DATE_FORMAT_BACKEND
    );
    const statusDate = metadata.lastStatusUpdatedTime
      ? moment(
          metadata.lastStatusUpdatedTime,
          AppConstants.DATE_FORMAT_BACKEND
        ).format(AppConstants.TIMESTAMP)
      : statuses && statuses.length
      ? moment(statuses[0].statusDate, AppConstants.DATE_FORMAT_BACKEND).format(
          AppConstants.TIMESTAMP
        )
      : "";
    const startTime = new Date(pickUpInformation.pickupSlot.startTime);
    const endTime = new Date(pickUpInformation.pickupSlot.endTime);
    transformedArray.push({
      id: id,
      collectionHubCode:
        pickUpInformation &&
        pickUpInformation.pickUpContactInfo &&
        pickUpInformation.pickUpContactInfo.companyName,
      collectionHubName:
        pickUpInformation &&
        pickUpInformation.pickUpContactInfo &&
        pickUpInformation.pickUpContactInfo.name,
      deliveryArea: customerContactInfo.address
        ? customerContactInfo.address.area
        : "",
      consignmentId: consignmentCode,
      deliveryStatus: metadata.deliveryStatus,
      orderId: orderCode,
      transactionDate: transactionDate.format(AppConstants.DATE_FORMAT),
      type: metadata.category,
      collectionDate: collectionDate.format(AppConstants.DATE_FORMAT),
      deliverySlot: `${localTimeString(startTime)} - ${localTimeString(
        endTime
      )}`,
      customerName: customerContactInfo.name,
      customerEmail: customerContactInfo.email,
      customerMobileNumber: customerContactInfo.phone,
      hubCode: store,
      hubName: storeName,
      statusDate: statusDate,
      numberOfItems:
        metadata.numberOfItems && metadata.numberOfItems > 0
          ? metadata.numberOfItems
          : products && products.length
          ? products.length
          : 0,
      paymentType: metadata.paymentType,
      cancellationReason: reason
        ? reason.reasonDescription
        : statuses && statuses.length && statuses[0].reason
        ? statuses[0].reason.reasonDescription
        : "",
    });
  });
  return [...transformedArray];
};

export const getCNCConsignmentStatusesField = (
  fieldName: string,
  statusCode: string
) => {
  const statuses: any = AppConstants.CNC_CONSTANTS.CONSIGNMENTS_DATA.STATUSES;
  return statusCode && statuses[statusCode]
    ? statuses[statusCode][fieldName]
    : "";
};

export const createFilterPayload = (
  payload: any,
  data?: any,
  isLoadMore?: boolean,
  isRoute?: boolean
) => {
  payload.attributes = [];
  if (payload.hub) {
    if (payload.hub.value) {
      let hubValue = payload.hub.value.map((item: any) => item.value);
      let hubNameValue = [];
      if (payload.hubName && payload.hubName.value.length) {
        hubNameValue = payload.hubName.value.map((item: any) => item.value);
      }
      hubValue = hubValue.concat(hubNameValue);
      const selectAllIdx = hubValue.indexOf(AppConstants.SELECT_ALL.value);
      const selectNoneIdx = hubValue.indexOf(AppConstants.SELECT_NONE.value);
      if (selectAllIdx > -1) {
        hubValue = [];
      }
      if (selectNoneIdx > -1) {
        hubValue = [];
      }
      payload.attributes.push({
        key: payload.hub.fieldName,
        value: getUniqueValues(hubValue),
      });
      delete payload.hub;
      delete payload.hubName;
    } else {
      payload.attributes.push({
        key: payload.hub.fieldName,
        value: [],
      });
      delete payload.hub;
      delete payload.hubName;
    }
  }
  if (payload.orderType) {
    let attrValue = [];
    if (payload.orderType.value) {
      attrValue = getFilterAttributeValue(payload.orderType.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.orderType.fieldName,
      value: attrValue,
    });
    delete payload.orderType;
  }
  if (payload.deliveryStatus) {
    let attrValue = [];
    if (payload.deliveryStatus.value) {
      attrValue = getFilterAttributeValue(payload.deliveryStatus.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.deliveryStatus.fieldName,
      value: attrValue,
    });
    delete payload.deliveryStatus;
  }
  if (payload.cancellationReason) {
    let attrValue = [];
    if (payload.cancellationReason.value) {
      attrValue = getFilterAttributeValue(payload.cancellationReason.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.cancellationReason.fieldName,
      value: attrValue,
    });
    delete payload.cancellationReason;
  }

  if (payload.collectionHubCode) {
    let attrValue = [];
    if (payload.collectionHubCode.value) {
      attrValue = getFilterAttributeValue(payload.collectionHubCode.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.collectionHubCode.fieldName,
      value: attrValue,
    });
    delete payload.collectionHubCode;
  }

  if (payload.collectionHubName) {
    let attrValue = [];
    if (payload.collectionHubName.value) {
      attrValue = getFilterAttributeValue(payload.collectionHubName.value);
    } else {
      attrValue = [];
    }
    payload.attributes.push({
      key: payload.collectionHubName.fieldName,
      value: attrValue,
    });
    delete payload.collectionHubName;
  }
  if (payload.modeOfCollection) {
    payload.attributes.push({
      key: payload.modeOfCollection.fieldName,
      value: payload.modeOfCollection.value || [],
    });
    delete payload.modeOfCollection;
  }
  if (payload.deliveryType) {
    payload.attributes.push({
      key: payload.deliveryType.fieldName,
      value: payload.deliveryType.value || [],
    });
    delete payload.deliveryType;
  }
  if (payload.displayRouteStatus) {
    payload.attributes.push({
      key: payload.displayRouteStatus.fieldName,
      value: payload.displayRouteStatus.value || [],
    });
    delete payload.displayRouteStatus;
  }
  if (payload.deliverySlot && payload.deliverySlot.value) {
    const delSlotValue = payload.deliverySlot.value.map(
      (item: any) => item.value
    );
    const dateRange = [];
    const selectAllIdx = delSlotValue.indexOf(AppConstants.SELECT_ALL.value);
    if (payload.dateRange && payload.dateRange.value) {
      const allDates = enumerateDaysBetweenDates(
        payload.dateRange.value.startDate,
        payload.dateRange.value.endDate
      );
      if (selectAllIdx > -1 || !delSlotValue.length) {
        payload.isDateRange = true;
        dateRange.push({
          startTime: moment(
            payload.dateRange.value.startDate,
            AppConstants.DATE_FORMAT
          )
            .hours(0)
            .minutes(0)
            .seconds(0)
            .format(AppConstants.DATE_FORMAT_BACKEND),
          endTime: moment(
            payload.dateRange.value.endDate,
            AppConstants.DATE_FORMAT
          )
            .hours(23)
            .minutes(59)
            .seconds(59)
            .format(AppConstants.DATE_FORMAT_BACKEND),
        });
      } else {
        payload.isDateRange = false;
        delSlotValue.forEach((slot: any) => {
          const splitSlot = slot.split("-");
          const startSlot = splitSlot[0].split(":");
          const endSlot = splitSlot[1].split(":");
          if (allDates.length) {
            allDates.forEach((date: string) => {
              dateRange.push({
                startTime: moment(date, AppConstants.DATE_FORMAT)
                  .hours(startSlot[0])
                  .minutes(startSlot[1])
                  .seconds(startSlot[2])
                  .format(AppConstants.DATE_FORMAT_BACKEND),
                endTime: moment(date, AppConstants.DATE_FORMAT)
                  .hours(endSlot[0])
                  .minutes(endSlot[1])
                  .seconds(endSlot[2])
                  .format(AppConstants.DATE_FORMAT_BACKEND),
              });
            });
          }
        });
      }
      if (payload.reset) {
        delete payload.isDateRange;
      }
    }
    payload.isCNC = true;
    payload.dateRange = dateRange;
    delete payload.displayRouteStatus;
    delete payload.deliverySlot;
    delete payload.reset;
  }
  if (isLoadMore && data) {
    payload.pageNumber =
      data.pageNumber + 1 < data.totalPages
        ? data.pageNumber + 1
        : payload.pageNumber;
  }
  if (data.continuationToken && payload.pageNumber) {
    payload.requestContinuationToken = data.continuationToken;
  }
  return payload;
};

export const createPayloadForExcelExport = (
  payload: any,
  data?: any,
  isLoadMore?: boolean,
  isRoute?: boolean
) => {
  const newPayload = createFilterPayload(payload, data, isLoadMore, isRoute);
  const orderFilterRequestDto = { orderFilterRequestDto: newPayload };
  return orderFilterRequestDto;
};

export const createCNCReplanningPayload = (params: any) => {
  const payload: any = {
    consignmentId: params.id,
  };
  payload.status = params.cncStatus;
  payload.comments = params.commentsValue;
  payload.reason = params.reason
    ? {
        reasonCode: params.reason.reasonCode,
        reasonDescription: params.reason.reasonDescription,
        deliveryStatus: params.reason.deliveryStatus,
      }
    : {};

  return payload;
};

export const checkTerminalStatus = (deliveryStatus: string) => {
  return (
    AppConstants.PLANNING_CONSTANTS.CONSIGNMENTS_DATA.TERMINAL_STATUSES.indexOf(
      deliveryStatus
    ) > -1
  );
};
